// ============================================
// Rotas: /login público; resto atrás de RequireAuth + AppShell.
// Seções do RAIZ na raiz do site (era /management/raiz no Aura).
// A rota :mac (detalhe do device) fica por último.
// ============================================

import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import AppShell from './components/AppShell'
import { RequireAuth } from './auth/RequireAuth'
import { useAuth } from './auth/AuthContext'

const RaizLayout = lazy(() => import('./pages/Raiz/RaizLayout'))
const RaizDashboard = lazy(() => import('./pages/Raiz/RaizDashboard'))
const RaizEvents = lazy(() => import('./pages/Raiz/RaizEvents'))
const RaizExcursions = lazy(() => import('./pages/Raiz/RaizExcursions'))
const RaizWarnings = lazy(() => import('./pages/Raiz/RaizWarnings'))
const RaizUnits = lazy(() => import('./pages/Raiz/RaizUnits'))
const RaizAlarmProfiles = lazy(() => import('./pages/Raiz/RaizAlarmProfiles'))
const RaizCompliance = lazy(() => import('./pages/Raiz/RaizCompliance'))
const RaizGap = lazy(() => import('./pages/Raiz/RaizGap'))
const RaizReport = lazy(() => import('./pages/Raiz/RaizReport'))
const RaizAbout = lazy(() => import('./pages/Raiz/RaizAbout'))
const RaizDeviceKeys = lazy(() => import('./pages/Raiz/RaizDeviceKeys'))
const RaizFirmware = lazy(() => import('./pages/Raiz/RaizFirmware'))
const RaizBench = lazy(() => import('./pages/Raiz/RaizBench'))
const RaizBattery = lazy(() => import('./pages/Raiz/RaizBattery'))
const RaizDeviceDetail = lazy(() => import('./pages/Raiz/RaizDeviceDetail'))
const RaizMap = lazy(() => import('./pages/Raiz/RaizMap'))
const RaizSettings = lazy(() => import('./pages/Raiz/RaizSettings'))
// Seções de Configurações (#131): cada uma é um chunk — quem abre Usuários não baixa Canais de alerta.
const SettingsWorkspace = lazy(() => import('./pages/Raiz/settings/SettingsWorkspace'))
const SettingsMaps = lazy(() => import('./pages/Raiz/settings/SettingsMaps'))
const SettingsUsers = lazy(() => import('./pages/Raiz/settings/SettingsUsers'))
const SettingsApiKeys = lazy(() => import('./pages/Raiz/settings/SettingsApiKeys'))
const SettingsAlerts = lazy(() => import('./pages/Raiz/settings/SettingsAlerts'))
const RaizAccount = lazy(() => import('./pages/Raiz/RaizAccount'))
const RaizCompanies = lazy(() => import('./pages/Raiz/RaizCompanies'))
const RaizTv = lazy(() => import('./pages/Raiz/RaizTv'))
const VerifyPage = lazy(() => import('./pages/Raiz/VerifyPage'))
// Telas públicas (produto Hakaru): chunks próprios com o campo WebGL e as
// fontes da marca — quem já está logado nunca baixa.
const LandingPage = lazy(() => import('./pages/Landing/LandingPage'))
const LoginPage = lazy(() => import('./auth/LoginPage'))

const Fallback = () => (
  <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
)

const AdminOnly = ({ children }: { children: JSX.Element }) => {
  const { isAdmin } = useAuth()
  return isAdmin ? children : <Navigate to="/" replace />
}

// F4 — visão sobre TODAS as empresas: a plataforma. SEC-002: inclui o
// platform_auditor (read-only) — ele VÊ o portfólio; as ações de escrita da
// página ficam atrás de isPlatformAdmin. O servidor também barra com 403; isto
// aqui é UX, não a fronteira de segurança.
const PlatformOnly = ({ children }: { children: JSX.Element }) => {
  const { isPlatformViewer } = useAuth()
  return isPlatformViewer ? children : <Navigate to="/" replace />
}

// Entrada do "/": deslogado vê a landing pública (produto Hakaru); logado
// entra no AppShell (dashboard + sub-rotas). Deep link protegido deslogado
// continua caindo no /login, preservando o destino.
const RootEntry = () => {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (user) return <AppShell />
  if (location.pathname === '/') return <LandingPage />
  return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
}

export default function App() {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Verificação pública de selo: fora do RequireAuth e do AppShell — funciona deslogado. */}
        <Route path="/verify/:id" element={<VerifyPage />} />
        {/* Modo TV: tela cheia, fora do RaizLayout/AppShell, ainda atrás de RequireAuth. */}
        <Route
          path="/tv"
          element={
            <RequireAuth>
              <RaizTv />
            </RequireAuth>
          }
        />
        <Route path="/" element={<RootEntry />}>
          <Route element={<RaizLayout />}>
            <Route index element={<RaizDashboard />} />
            {/* Eventos: Excursões (fila com disposição) e Avisos (estado dos sensores), cada aba com URL própria. */}
            <Route path="events" element={<RaizEvents />}>
              <Route index element={<Navigate to="excursions" replace />} />
              <Route path="excursions" element={<RaizExcursions />} />
              <Route path="warnings" element={<RaizWarnings />} />
              <Route path="*" element={<Navigate to="/events" replace />} />
            </Route>
            {/* Endereço antigo da fila: WhatsApp, Web Push e favoritos já enviados apontam para cá. Não remover. */}
            <Route path="excursions" element={<Navigate to="/events/excursions" replace />} />
            <Route path="map" element={<RaizMap />} />
            <Route path="units" element={<RaizUnits />} />
            <Route path="alarm-profiles" element={<RaizAlarmProfiles />} />
            <Route path="compliance" element={<RaizCompliance />} />
            <Route path="gap" element={<RaizGap />} />
            <Route path="report" element={<RaizReport />} />
            <Route path="about" element={<RaizAbout />} />
            <Route
              path="device-keys"
              element={
                <AdminOnly>
                  <RaizDeviceKeys />
                </AdminOnly>
              }
            />
            <Route
              path="firmware"
              element={
                <AdminOnly>
                  <RaizFirmware />
                </AdminOnly>
              }
            />
            <Route
              path="bench"
              element={
                <PlatformOnly>
                  <RaizBench />
                </PlatformOnly>
              }
            />
            <Route
              path="battery"
              element={
                <PlatformOnly>
                  <RaizBattery />
                </PlatformOnly>
              }
            />
            {/* Hub de Configurações (#131): a moldura é adminOnly; as seções são
                sub-rotas com URL própria. O índice (/settings) é resolvido pelo hub
                (redireciona no desktop, lista no celular); slug desconhecido volta ao índice. */}
            <Route
              path="settings"
              element={
                <AdminOnly>
                  <RaizSettings />
                </AdminOnly>
              }
            >
              <Route path="workspace" element={<SettingsWorkspace />} />
              <Route path="maps" element={<SettingsMaps />} />
              <Route path="users" element={<SettingsUsers />} />
              <Route path="api-keys" element={<SettingsApiKeys />} />
              <Route path="alerts" element={<SettingsAlerts />} />
              <Route path="*" element={<Navigate to="/settings" replace />} />
            </Route>
            {/* Minha conta (#131): qualquer papel logado — chega pelo menu do avatar. */}
            <Route path="account" element={<RaizAccount />} />
            <Route
              path="companies"
              element={
                <PlatformOnly>
                  <RaizCompanies />
                </PlatformOnly>
              }
            />
            <Route path=":mac" element={<RaizDeviceDetail />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
